import { Link, useParams, Navigate } from "react-router-dom";
import { LESSONS } from "../data/lessons";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { useEffect, useState } from "react";

const STORAGE_KEY = "ao_lesson_complete";

export default function LessonDetail() {
  const { slug } = useParams();
  const lesson = LESSONS.find((l) => l.slug === slug);
  const idx = LESSONS.findIndex((l) => l.slug === slug);
  const prev = idx > 0 ? LESSONS[idx - 1] : null;
  const next = idx >= 0 && idx < LESSONS.length - 1 ? LESSONS[idx + 1] : null;
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!slug) return;
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      setDone(!!saved[slug]);
    } catch (_) { /* ignore */ }
  }, [slug]);

  if (!lesson) return <Navigate to="/lessons" replace />;

  const toggleDone = () => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
      saved[slug] = !done;
      localStorage.setItem(STORAGE_KEY, JSON.stringify(saved));
      setDone(!done);
    } catch (_) { /* ignore */ }
  };

  return (
    <article className="max-w-3xl mx-auto px-6 py-16 lg:py-24" data-testid="lesson-detail-page">
      <Link to="/lessons" className="eyebrow inline-flex items-center gap-2 text-[#78716C] hover:text-[#1A1915]" data-testid="lesson-back-link">
        <ArrowLeft className="w-3 h-3" strokeWidth={1.5} /> All lessons
      </Link>
      <div className="mt-8 flex items-center justify-between">
        <div className="eyebrow">Chapter {String(lesson.n).padStart(2, "0")} · {LESSONS.length} total</div>
        <button
          type="button"
          onClick={toggleDone}
          className={`text-xs uppercase tracking-widest border px-3 py-2 flex items-center gap-2 transition-colors duration-150 ${done ? "bg-[#1A1915] text-[#FAFAF9] border-[#1A1915]" : "border-[#1A1915] text-[#1A1915] hover:bg-[#F5F5F4]"}`}
          data-testid="lesson-mark-complete"
        >
          <Check className="w-3 h-3" strokeWidth={2} /> {done ? "Completed" : "Mark complete"}
        </button>
      </div>
      <h1 className="mt-6 font-serif text-4xl lg:text-6xl text-[#1A1915] leading-[0.95]">{lesson.title}</h1>
      <p className="mt-6 font-serif italic text-2xl text-[#78716C] leading-snug">{lesson.teaser}</p>

      <div className="mt-12 space-y-6">
        {lesson.body.map((p, i) => (
          <p key={i} className="text-lg text-[#1A1915] leading-relaxed">{p}</p>
        ))}
      </div>

      {/* Closing impulse */}
      <div className="mt-16 border-t-4 border-[#B8863C] bg-[#F5F5F4]/50 border-l border-r border-b border-[#1A1915] p-8" data-testid="closing-impulse">
        <div className="eyebrow text-[#B8863C]">Closing Impulse</div>
        <p className="mt-4 font-serif text-2xl text-[#1A1915] leading-snug">{lesson.impulse}</p>
      </div>

      {/* Prev/Next */}
      <div className="mt-16 grid grid-cols-2 gap-4 border-t border-[#1A1915]/15 pt-8">
        <div>
          {prev && (
            <Link to={`/lessons/${prev.slug}`} className="block group" data-testid="lesson-prev">
              <div className="eyebrow text-[#78716C] group-hover:text-[#1A1915]">← Previous</div>
              <div className="mt-2 font-serif text-lg text-[#1A1915] leading-tight">{prev.title}</div>
            </Link>
          )}
        </div>
        <div className="text-right">
          {next && (
            <Link to={`/lessons/${next.slug}`} className="block group" data-testid="lesson-next">
              <div className="eyebrow text-[#78716C] group-hover:text-[#1A1915]">Next →</div>
              <div className="mt-2 font-serif text-lg text-[#1A1915] leading-tight">{next.title}</div>
            </Link>
          )}
        </div>
      </div>
    </article>
  );
}
